import type { Database } from '#shared/types/database.types'

type Camp = Database['public']['Tables']['vzw_camps']['Row']
type TicketType = Database['public']['Tables']['vzw_ticket_types']['Row']

export enum CampStage {
  DRAFT = 'draft',
  CANCELLED = 'cancelled',
  PRE_REGISTRATION = 'pre_registration',
  COMING_SOON = 'coming_soon', // Published but ticket sales haven't started yet
  TICKET_SALES = 'ticket_sales',
  LIMITED = 'limited', // Some tickets sold out but others still available (e.g., week sold out, day remains)
  SOLD_OUT = 'sold_out',
  WAITLIST = 'waitlist',
  ONGOING = 'ongoing',
  ENDED = 'ended',
  ARCHIVED = 'archived',
}

export interface StageInfo {
  stage: CampStage
  label: string
  color: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'
  icon: string
  priority: number // Higher = more important to display
}

export interface ActiveTicketInfo {
  ticketType: TicketType
  isActive: boolean | null
  isSoldOut: boolean
  remainingSpots: number
}

export const useCampStages = () => {
  /**
   * Get currently active ticket types for a camp
   */
  const getActiveTickets = (
    tickets: TicketType[],
    camp: Camp,
  ): ActiveTicketInfo[] => {
    const now = new Date()

    // Calculate event-level remaining spots (master constraint)
    const eventCapacity = camp.max_attendees ?? 0
    const eventBooked = camp.current_attendees ?? 0
    const eventRemaining = Math.max(0, eventCapacity - eventBooked)

    return tickets.map(ticket => {
      const saleStart = ticket.sale_start_date
        ? new Date(ticket.sale_start_date)
        : null

      const saleEnd = ticket.sale_end_date
        ? new Date(ticket.sale_end_date)
        : null

      // HYBRID CAPACITY CALCULATION:
      // Event capacity is the master limit (always enforced)
      // Ticket capacity is optional sub-limit (NULL = unlimited within event capacity)
      let remainingSpots = eventRemaining

      // If ticket has a specific capacity limit, apply it
      if (ticket.capacity !== null && ticket.capacity !== undefined) {
        const ticketCapacity = ticket.capacity
        const ticketSold = (ticket.sold || 0) + (ticket.reserved_quantity || 0)
        const ticketRemaining = Math.max(0, ticketCapacity - ticketSold)

        // Use the most restrictive limit (smallest positive number)
        remainingSpots = Math.min(eventRemaining, ticketRemaining)
      }

      // Ticket is active if:
      // 1. is_active flag is true (or undefined/null = true by default)
      // 2. Sale has started (if sale_start_date is set, must be in past/present)
      // 3. Sale hasn't ended (if sale_end_date is set, must be in future/present)
      // 4. Not sold out (remaining spots > 0 considering BOTH event and ticket capacity)
      const isActive
        = (ticket.is_active ?? true)
          && (!saleStart || now >= saleStart) // No start date = always started
          && (!saleEnd || now <= saleEnd) // No end date = never ends
          && remainingSpots > 0

      const isSoldOut = remainingSpots <= 0

      return {
        ticketType: ticket,
        isActive,
        isSoldOut,
        remainingSpots,
      }
    })
  }

  /**
   * Check if any tickets are currently on sale
   * Uses hybrid capacity: event capacity as MASTER limit
   * If event has capacity, tickets are available even if specific types hit their limits
   */
  const hasActiveTickets = (tickets: TicketType[], camp: Camp): boolean => {
    if (tickets.length === 0)
      return false

    // In hybrid system, check event capacity FIRST (master constraint)
    const eventCapacity = camp.max_attendees ?? 0
    const eventBooked = camp.current_attendees ?? 0
    const eventRemaining = Math.max(0, eventCapacity - eventBooked)

    // If event has no remaining capacity, no tickets are available
    if (eventRemaining <= 0)
      return false

    // Event has capacity - now check if any tickets are active
    const activeTickets = getActiveTickets(tickets, camp)

    // In hybrid system: if event has capacity but all tickets hit their individual limits,
    // we should still allow sales (admin can increase ticket capacity or add new tickets)
    // So return true if event has capacity AND at least one ticket is not time-restricted
    const hasTimeActiveTickets = activeTickets.some(t => {
      const now = new Date()

      const saleStart = t.ticketType.sale_start_date
        ? new Date(t.ticketType.sale_start_date)
        : null

      const saleEnd = t.ticketType.sale_end_date
        ? new Date(t.ticketType.sale_end_date)
        : null

      const isTimeValid
        = (t.ticketType.is_active ?? true)
          && (!saleStart || now >= saleStart)
          && (!saleEnd || now <= saleEnd)

      return isTimeValid
    })

    // If event has capacity and any ticket is within its sale period, consider tickets available
    const result = eventRemaining > 0 && hasTimeActiveTickets

    return result
  }

  /**
   * Check if all tickets are sold out
   * Uses hybrid capacity: ONLY returns true if event itself is at capacity
   * Individual ticket capacity limits don't matter if event has remaining capacity
   */
  const areAllTicketsSoldOut = (tickets: TicketType[], camp: Camp): boolean => {
    if (tickets.length === 0)
      return false

    // In hybrid capacity system, event capacity is the MASTER limit
    // Only truly "sold out" if the event itself has no remaining capacity
    const eventCapacity = camp.max_attendees ?? 0
    const eventBooked = camp.current_attendees ?? 0
    const eventSoldOut = eventBooked >= eventCapacity

    // Return true ONLY if event capacity is exhausted
    // Individual ticket capacities are sub-limits and don't define "sold out"
    return eventSoldOut
  }

  /**
   * Check if ticket sales are scheduled but haven't started yet
   * Returns true if ALL tickets have a sale_start_date in the future
   * Uses hybrid capacity: returns false if event already sold out
   */
  const areTicketSalesPending = (
    tickets: TicketType[],
    camp: Camp,
  ): boolean => {
    if (tickets.length === 0)
      return false

    // Check event-level capacity first
    // If event is already sold out, no pending sales are possible
    const eventCapacity = camp.max_attendees ?? 0
    const eventBooked = camp.current_attendees ?? 0
    if (eventBooked >= eventCapacity)
      return false

    const now = new Date()

    // Check if any ticket has a sale period that hasn't started
    return tickets.every(ticket => {
      // If ticket is not active, skip it
      if (ticket.is_active === false)
        return true

      // If no sale_start_date, ticket is immediately available (not pending)
      if (!ticket.sale_start_date)
        return false

      const saleStart = new Date(ticket.sale_start_date)

      return now < saleStart
    })
  }

  /**
   * Get the earliest ticket sale start date
   */
  const getEarliestTicketSaleDate = (tickets: TicketType[]): Date | null => {
    const activeDates = tickets
      .filter(t => t.is_active !== false && t.sale_start_date)
      .map(t => new Date(t.sale_start_date!))

    if (activeDates.length === 0)
      return null

    return new Date(Math.min(...activeDates.map(d => d.getTime())))
  }

  /**
   * Get all current stages for a camp (can have multiple simultaneous stages)
   */
  const getCampStages = (
    camp: Camp,
    tickets: TicketType[] = [],
  ): StageInfo[] => {
    const stages: StageInfo[] = []
    const now = new Date()

    // Stage 0: Status-based stages (Draft, Cancelled, Archived)
    if (camp.status === 'draft') {
      stages.push({
        stage: CampStage.DRAFT,
        label: 'Concept',
        color: 'warning',
        icon: 'bx-pencil',
        priority: 100,
      })

      return stages // Draft camps don't have other stages
    }

    if (camp.status === 'cancelled') {
      stages.push({
        stage: CampStage.CANCELLED,
        label: 'Geannuleerd',
        color: 'error',
        icon: 'bx-x-circle',
        priority: 100,
      })

      return stages // Cancelled camps don't have other stages
    }

    if (camp.status === 'completed') {
      stages.push({
        stage: CampStage.ARCHIVED,
        label: 'Gearchiveerd',
        color: 'secondary',
        icon: 'bx-archive',
        priority: 100,
      })

      return stages // Archived camps don't have other stages
    }

    // For published camps, check multiple possible concurrent stages

    // Stage 5: Event ended
    if (camp.end_date && new Date(camp.end_date) < now) {
      stages.push({
        stage: CampStage.ENDED,
        label: 'Afgelopen',
        color: 'secondary',
        icon: 'bx-calendar-x',
        priority: 90,
      })
    }

    // Stage 4: Event ongoing (can overlap with ticket sales)
    if (camp.start_date && camp.end_date) {
      const eventStart = new Date(camp.start_date)
      const eventEnd = new Date(camp.end_date)

      if (now >= eventStart && now <= eventEnd) {
        stages.push({
          stage: CampStage.ONGOING,
          label: 'Bezig',
          color: 'info',
          icon: 'bx-time',
          priority: 60,
        })
      }
    }

    // Stage 2: Ticket sales active (HIGHEST PRIORITY - tickets available for purchase)
    const hasActiveTicketsResult = hasActiveTickets(tickets, camp)

    if (hasActiveTicketsResult) {
      stages.push({
        stage: CampStage.TICKET_SALES,
        label: 'Tickets Open',
        color: 'success',
        icon: 'bx-cart',
        priority: 85, // Higher priority than waitlist
      })
    }

    // Stage 2b: Ticket sales pending (published but sales haven't started)
    else if (tickets.length > 0 && areTicketSalesPending(tickets, camp)) {
      stages.push({
        stage: CampStage.COMING_SOON,
        label: 'Binnenkort',
        color: 'info',
        icon: 'bx-calendar-event',
        priority: 70,
      })
    }

    // Stage 3: Waitlist active (when sold out and waitlist enabled)
    else if (areAllTicketsSoldOut(tickets, camp) && camp.enable_waitlist) {
      stages.push({
        stage: CampStage.WAITLIST,
        label: 'Wachtlijst',
        color: 'warning',
        icon: 'bx-list-ul',
        priority: 80,
      })
    }

    // Sold out without waitlist
    else if (areAllTicketsSoldOut(tickets, camp) && !camp.enable_waitlist) {
      stages.push({
        stage: CampStage.SOLD_OUT,
        label: 'Volgeboekt',
        color: 'error',
        icon: 'bx-user-x',
        priority: 80,
      })
    }

    // Stage 1: Pre-registration open
    // Check if we're in the pre-registration period
    if (camp.registration_start_date) {
      const regStart = new Date(camp.registration_start_date)

      // Pre-registration is active if:
      // 1. registration_start_date is set and we're past it
      // 2. AND ticket sales haven't started yet (either camp-level or ticket-level)
      if (now >= regStart) {
        const campSaleStart = camp.ticket_sale_start_date
          ? new Date(camp.ticket_sale_start_date)
          : null

        const beforeCampSaleStart = campSaleStart && now < campSaleStart

        const ticketSalesPending
          = tickets.length > 0 && areTicketSalesPending(tickets, camp)

        // Show pre-registration if ticket sales haven't started
        if (beforeCampSaleStart || ticketSalesPending) {
          stages.push({
            stage: CampStage.PRE_REGISTRATION,
            label: 'Pre-registratie',
            color: 'info',
            icon: 'bx-envelope',
            priority: 75,
          })
        }
      }
    }

    // If published but no other stages, show as published (coming soon)
    if (stages.length === 0 && camp.status === 'published') {
      stages.push({
        stage: CampStage.COMING_SOON,
        label: 'Gepubliceerd',
        color: 'primary',
        icon: 'bx-check-circle',
        priority: 50,
      })
    }

    // Sort by priority (descending) - most important first
    return stages.sort((a, b) => b.priority - a.priority)
  }

  /**
   * Get display configuration for stage chips
   * Returns up to 3 most important stages for display
   */
  const getStageDisplay = (
    camp: Camp,
    tickets: TicketType[] = [],
    maxStages: number = 3,
  ) => {
    const allStages = getCampStages(camp, tickets)
    const visibleStages = allStages.slice(0, maxStages)
    const hiddenStages = allStages.slice(maxStages)

    return {
      visible: visibleStages,
      hidden: hiddenStages,
      total: allStages.length,
    }
  }

  /**
   * Check if camp is visible to public (end users)
   */
  const isPubliclyVisible = (camp: Camp): boolean => {
    if (camp.status !== 'published')
      return false

    // Don't show ended camps
    if (camp.end_date && new Date(camp.end_date) < new Date())
      return false

    return true
  }

  /**
   * Get primary action for camp based on current stage
   */
  const getPrimaryAction = (camp: Camp, tickets: TicketType[]) => {
    const stages = getCampStages(camp, tickets)

    const primaryStage = stages[0]

    if (!primaryStage)
      return null

    switch (primaryStage.stage) {
      case CampStage.PRE_REGISTRATION:
        return {
          label: 'Hou me op de hoogte',
          text: 'Hou me op de hoogte',
          action: 'pre-register',
          icon: 'bx-envelope',
          color: 'primary',
        }
      case CampStage.TICKET_SALES:
        return {
          label: 'Boek nu',
          text: 'Boek nu',
          action: 'book',
          icon: 'bx-cart',
          color: 'primary',
        }
      case CampStage.LIMITED:
        return {
          label: 'Boek nu',
          text: 'Boek nu',
          action: 'book',
          icon: 'bx-cart',
          color: 'primary',
          showWaitlist: camp.enable_waitlist, // Signal to show secondary waitlist button
        }
      case CampStage.COMING_SOON: {
        // Show when ticket sales will start
        const saleDate = getEarliestTicketSaleDate(tickets)

        const dateText = saleDate
          ? saleDate.toLocaleDateString('nl-BE', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })
          : 'binnenkort'

        return {
          label: 'Tickets binnenkort beschikbaar',
          text: `Tickets beschikbaar vanaf ${dateText}`,
          action: 'coming-soon',
          icon: 'bx-calendar-event',
          color: 'info',
          disabled: true,
        }
      }
      case CampStage.WAITLIST:
      case CampStage.SOLD_OUT:
        if (camp.enable_waitlist) {
          return {
            label: 'Doe mee aan wachtlijst',
            text: 'Doe mee aan wachtlijst',
            action: 'waitlist',
            icon: 'bx-list-ul',
            color: 'warning',
          }
        }

        return {
          label: 'Volgeboekt',
          text: 'Volgeboekt',
          action: 'sold-out',
          icon: 'bx-user-x',
          color: 'error',
          disabled: true,
        }
      case CampStage.ENDED:
      case CampStage.ARCHIVED:
        return null
      default:
        return null
    }
  }

  return {
    CampStage,
    getActiveTickets,
    hasActiveTickets,
    areAllTicketsSoldOut,
    areTicketSalesPending,
    getEarliestTicketSaleDate,
    getCampStages,
    getStageDisplay,
    isPubliclyVisible,
    getPrimaryAction,
  }
}
